import './FilterSection.scss';
import classNames from 'classnames';
import { prefixClass } from 'lib/utils';
import type { ReactNode } from 'react';
import { IconChevron, IconChevronDown } from 'components/Icons';
import { CountBadge } from '../../../Common';

interface FilterSectionProps {
  /** Section title */
  title: string;
  /** Icon to display next to title */
  icon?: ReactNode;
  /** Whether the section is expanded */
  expanded?: boolean;
  /** Callback when expand/collapse is toggled */
  onToggle?: () => void;
  /** Section content */
  children: ReactNode;
  /** Additional class name */
  className?: string;
  /** Blocker count (shown with alert styling when collapsed) */
  blockerCount?: number;
  /** Regular count (shown when collapsed) */
  count?: number;
}

/**
 * FilterSection - A collapsible container for filter groups
 * Used for top-level sections like To-Dos, KAI, Calendar, Conversations
 */
const FilterSection = ({
  title,
  icon,
  expanded = true,
  onToggle,
  children,
  className,
  blockerCount,
  count,
}: FilterSectionProps) => {
  const prefix = prefixClass('q2-filter-section');

  // Only show counts when section is collapsed
  const showBlockerCount = !expanded && blockerCount !== undefined && blockerCount > 0;
  const showCount = !expanded && count !== undefined && count > 0;

  return (
    <div className={classNames(prefix(), { expanded }, className)}>
      <div
        className={prefix('header')}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle?.();
          }
        }}
      >
        <div className={prefix('header-left')}>
          {icon && <div className={prefix('header-icon')}>{icon}</div>}
          <span className={prefix('header-title')}>{title}</span>
        </div>
        <div className={prefix('header-right')}>
          {showBlockerCount && (
            <div className={prefix('header-blocker-count')}>
              <CountBadge count={blockerCount} />
            </div>
          )}
          {showCount && <CountBadge count={count} />}
          <div className={prefix('header-chevron')}>{expanded ? <IconChevronDown /> : <IconChevron />}</div>
        </div>
      </div>
      {expanded && <div className={prefix('content')}>{children}</div>}
    </div>
  );
};

export default FilterSection;
